import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { lazy, Suspense } from 'react';

const ProposalPage = lazy(() => import('@/pages/ProposalPage'));
const AdminLayout = lazy(() => import('@/layouts/AdminLayout'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/admin/DashboardPage'));
const ProposalsPage = lazy(() => import('@/pages/admin/ProposalsPage'));
const ProposalEditPage = lazy(() => import('@/pages/admin/ProposalEditPage'));
const ClientsPage = lazy(() => import('@/pages/admin/ClientsPage'));
const ClientEditPage = lazy(() => import('@/pages/admin/ClientEditPage'));
const ServicesPage = lazy(() => import('@/pages/admin/ServicesPage'));
const PlansPage = lazy(() => import('@/pages/admin/PlansPage'));
const GalleryPage = lazy(() => import('@/pages/admin/GalleryPage'));
const FaqPage = lazy(() => import('@/pages/admin/FaqPage'));
const BenefitsPage = lazy(() => import('@/pages/admin/BenefitsPage'));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'));
const NotificationsPage = lazy(() => import('@/pages/admin/NotificationsPage'));
const SettingsPage = lazy(() => import('@/pages/admin/SettingsPage'));

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500 dark:text-slate-400">Cargando...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (isAuthenticated) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/:slug" element={<ProposalPage />} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="propuestas" element={<ProposalsPage />} />
          <Route path="propuestas/:id" element={<ProposalEditPage />} />
          <Route path="clientes" element={<ClientsPage />} />
          <Route path="clientes/:id" element={<ClientEditPage />} />
          <Route path="servicios" element={<ServicesPage />} />
          <Route path="planes" element={<PlansPage />} />
          <Route path="galeria" element={<GalleryPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="beneficios" element={<BenefitsPage />} />
          <Route path="usuarios" element={<UsersPage />} />
          <Route path="notificaciones" element={<NotificationsPage />} />
          <Route path="configuracion" element={<SettingsPage />} />
        </Route>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}